import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdArrowBack, MdRocketLaunch } from "react-icons/md";
import { HiWrenchScrewdriver } from "react-icons/hi2";

// Converts route path → readable title
// "/fee-management" → "Fee Management"
const routeToTitle = (pathname) => {
  const segment = pathname.split("/").filter(Boolean).pop() || "This Page";
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const CommingSoon = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pageName = routeToTitle(location.pathname);

  const [progress, setProgress] = useState(0);
  const [dotCount, setDotCount] = useState(1);

  // Fake progress bar animation
  useEffect(() => {
    const targets = [20, 45, 63, 78, 88];
    let i = 0;
    const interval = setInterval(() => {
      if (i < targets.length) {
        setProgress(targets[i]);
        i++;
      } else {
        clearInterval(interval);
      }
    }, 600);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((d) => (d >= 3 ? 1 : d + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center overflow-hidden relative">

      <div className="absolute top-[-80px] right-[-80px] w-[340px] h-[340px] rounded-full bg-[#ffe4e9] opacity-60 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-60px] left-[-60px] w-[260px] h-[260px] rounded-full bg-[#ffe4e9] opacity-40 blur-3xl pointer-events-none" />

      <div className="absolute top-8 left-8 grid grid-cols-5 gap-2 opacity-20 pointer-events-none">
        {Array.from({ length: 25 }).map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#CC234F]" />
        ))}
      </div>

      <div className="absolute bottom-8 right-8 grid grid-cols-5 gap-2 opacity-20 pointer-events-none">
        {Array.from({ length: 25 }).map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#CC234F]" />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-lg text-center">

        <div className="flex items-center justify-center mb-8">
          <div className="relative">
            <div
              className="absolute inset-0 rounded-full bg-[#CC234F] opacity-10"
              style={{ animation: "ping 2s cubic-bezier(0,0,0.2,1) infinite" }}
            />
            <div
              className="absolute inset-[-10px] rounded-full border-2 border-[#CC234F] opacity-20"
              style={{ animation: "ping 2s 0.4s cubic-bezier(0,0,0.2,1) infinite" }}
            />

            <div
              className="relative w-24 h-24 rounded-2xl bg-white shadow-lg border border-[#ffe4e9] flex items-center justify-center"
              style={{ animation: "float 3s ease-in-out infinite" }}
            >
              <HiWrenchScrewdriver className="text-[#CC234F]" size={42} />
            </div>

            <div
              className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-[#CC234F] flex items-center justify-center shadow-md"
              style={{ animation: "bounce 1.4s ease-in-out infinite" }}
            >
              <MdRocketLaunch className="text-white" size={16} />
            </div>
          </div>
        </div>

        <div
          className="inline-flex items-center gap-2 bg-[#ffe4e9] text-[#CC234F] text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest"
          style={{ animation: "fadeSlideUp 0.5s ease forwards", opacity: 0 }}
        >
          <span
            className="w-2 h-2 rounded-full bg-[#CC234F] inline-block"
            style={{ animation: "pulse 1.5s ease-in-out infinite" }}
          />
          Under Development
        </div>

        <h1
          className="text-4xl font-bold text-[#222222] mb-3 leading-tight"
          style={{ animation: "fadeSlideUp 0.5s 0.1s ease forwards", opacity: 0 }}
        >
          {pageName}
        </h1>

        <p
          className="text-secondaryTextColor text-base mb-8"
          style={{ animation: "fadeSlideUp 0.5s 0.2s ease forwards", opacity: 0 }}
        >
          We're crafting something great here.
          <br />
          This page will be available very soon.
        </p>

        <div
          className="mb-2"
          style={{ animation: "fadeSlideUp 0.5s 0.3s ease forwards", opacity: 0 }}
        >
          <div className="flex justify-between text-xs text-gray-400 mb-1.5 font-medium">
            <span>Building{".".repeat(dotCount)}</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full h-2.5 bg-[#ffe4e9] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#CC234F] to-[#e8567a]"
              style={{
                width: `${progress}%`,
                transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </div>
        </div>

        <div
          className="grid grid-cols-3 gap-3 my-8"
          style={{ animation: "fadeSlideUp 0.5s 0.4s ease forwards", opacity: 0 }}
        >
          {[
            { label: "Design", done: true },
            { label: "Development", done: progress >= 50 },
            { label: "Launch", done: false },
          ].map((step, i) => (
            <div
              key={i}
              className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-semibold transition-all duration-500
                ${step.done
                  ? "bg-[#ffe4e9] border-[#CC234F] text-[#CC234F]"
                  : "bg-white border-gray-200 text-gray-400"
                }`}
            >
              <div
                className={`w-2.5 h-2.5 rounded-full ${step.done ? "bg-[#CC234F]" : "bg-gray-300"}`}
                style={step.done ? { animation: "pulse 1.5s ease-in-out infinite" } : {}}
              />
              {step.label}
            </div>
          ))}
        </div>

        <div
          style={{ animation: "fadeSlideUp 0.5s 0.5s ease forwards", opacity: 0 }}
        >
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#CC234F] hover:bg-[#a81c3e] text-white text-sm font-semibold rounded-xl shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
          >
            <MdArrowBack size={18} />
            Go Back
          </button>
        </div>

      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CommingSoon;